import { GITHUB_GENERIC_MAPPINGS_BY_KEY } from './indexMappings/genericMappings/GITHUB_GENERIC_MAPPINGS'
import { JIRA_GENERIC_MAPPINGS_BY_KEY } from './indexMappings/genericMappings/JIRA_GENERIC_MAPPINGS'
import { LINEAR_GENERIC_MAPPINGS_BY_KEY } from './indexMappings/genericMappings/LINEAR_GENERIC_MAPPINGS'
import { genericMappingToSpecificField } from './indexMappings/genericMappings/utils'
import { SuperSource } from './super'

type UrlResolution = {
  source: SuperSource
  /** Direct document ID match (e.g. Slack thread timestamp) */
  id?: string
  /** Container ID (channel, repo, project) */
  rootId?: string
  /** Date range fallback for daily-aggregated docs */
  dateRange?: { after: string; before: string }
  /** Free-text search term when exact id/rootId can't be extracted from URL */
  searchText?: string
  /** Exact term filters on specific ES fields (e.g. generic prop fields) */
  termFilters?: Record<string, string>
}

type UrlResolver = {
  patterns: RegExp[]
  resolve: (match: RegExpMatchArray) => UrlResolution
}

function startOfDay(date: Date): string {
  const d = new Date(date)
  d.setUTCHours(0, 0, 0, 0)
  return d.toISOString()
}

function endOfDay(date: Date): string {
  const d = new Date(date)
  d.setUTCHours(23, 59, 59, 999)
  return d.toISOString()
}

/** Convert 32 hex chars to UUID format (8-4-4-4-12) */
function hexToUuid(hex: string): string {
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
    12,
    16
  )}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/**
 * SuperSource → URL resolver
 *  — use `null` for sources with no URL-based lookup
 */
const resolversBySource: Record<SuperSource, UrlResolver | null> = {
  // Slack: https://{team}.slack.com/archives/{channelId}/p{timestamp}
  [SuperSource.SLACK_CHANNEL]: {
    patterns: [/^https?:\/\/[^/]+\.slack\.com\/archives\/([A-Z0-9_]+)\/p(\d+)/],
    resolve: (match) => {
      const channelId = match[1]!
      const rawTs = match[2]!
      // Convert Slack URL timestamp to TS format: insert dot before last 6 digits
      const slackTs = `${rawTs.slice(0, -6)}.${rawTs.slice(-6)}`
      // Extract date for daily doc fallback
      const epochSeconds = parseInt(rawTs.substring(0, 10), 10)
      const date = new Date(epochSeconds * 1000)
      return {
        source: SuperSource.SLACK_CHANNEL,
        id: slackTs,
        rootId: channelId,
        dateRange: {
          after: startOfDay(date),
          before: endOfDay(date),
        },
      }
    },
  },

  // GitHub: https://github.com/{owner}/{repo}/issues/{num} or /pull/{num}
  // ES id is a numeric GitHub node ID (not the PR/issue number).
  // rootId = "owner/repo", number matched via GITHUB_GENERIC_MAPPINGS.
  [SuperSource.GITHUB]: {
    patterns: [/^https?:\/\/github\.com\/([^/]+\/[^/]+)\/(issues|pull)\/(\d+)/],
    resolve: (match) => ({
      source: SuperSource.GITHUB,
      rootId: match[1]!,
      termFilters: {
        [genericMappingToSpecificField(GITHUB_GENERIC_MAPPINGS_BY_KEY.number)]:
          match[3]!,
      },
    }),
  },

  // GitLab: ES rootId/id are numeric internal IDs, not derivable from URL.
  [SuperSource.GITLAB]: null,

  // Linear: https://linear.app/{org}/issue/{KEY-NUM}/{optional-slug}
  // ES id is a UUID, not the issue key.
  [SuperSource.LINEAR]: {
    patterns: [/^https?:\/\/linear\.app\/[^/]+\/issue\/([A-Z]+-\d+)/],
    resolve: (match) => ({
      source: SuperSource.LINEAR,
      termFilters: {
        [genericMappingToSpecificField(
          LINEAR_GENERIC_MAPPINGS_BY_KEY.identifier
        )]: match[1]!,
      },
    }),
  },

  // Jira: https://{site}.atlassian.net/browse/{KEY-NUM}
  // ES id is numeric (Jira internal ID), not the issue key.
  [SuperSource.JIRA]: {
    patterns: [/^https?:\/\/[^/]+\.atlassian\.net\/browse\/([A-Z]+-\d+)/],
    resolve: (match) => ({
      source: SuperSource.JIRA,
      termFilters: {
        [genericMappingToSpecificField(JIRA_GENERIC_MAPPINGS_BY_KEY.key)]:
          match[1]!,
      },
    }),
  },

  // Confluence: https://{site}.atlassian.net/wiki/spaces/{spaceKey}/pages/{pageId}
  [SuperSource.CONFLUENCE]: {
    patterns: [
      /^https?:\/\/[^/]+\.atlassian\.net\/wiki\/spaces\/[^/]+\/pages\/(\d+)/,
    ],
    resolve: (match) => ({
      source: SuperSource.CONFLUENCE,
      id: match[1]!,
    }),
  },

  // Notion: https://notion.so/{optional-slug}-{pageId} (32 hex chars at end)
  // ES id stores UUID with hyphens; URL contains hex without hyphens.
  [SuperSource.NOTION]: {
    patterns: [/^https?:\/\/(?:www\.)?notion\.so\/(?:.*-)?([a-f0-9]{32})/],
    resolve: (match) => ({
      source: SuperSource.NOTION,
      id: hexToUuid(match[1]!),
    }),
  },

  // Google Drive: https://drive.google.com/file/d/{fileId} or /document/d/{docId}
  [SuperSource.GOOGLE_DRIVE]: {
    patterns: [
      /^https?:\/\/(?:docs|drive)\.google\.com\/(?:file|document|spreadsheets|presentation)\/d\/([^/]+)/,
    ],
    resolve: (match) => ({
      source: SuperSource.GOOGLE_DRIVE,
      id: match[1]!,
    }),
  },

  // Asana: https://app.asana.com/0/{projectId}/{taskId} (legacy)
  //   or:  https://app.asana.com/1/{orgId}/project/{projectId}/task/{taskId} (new)
  [SuperSource.ASANA]: {
    patterns: [
      /^https?:\/\/app\.asana\.com\/1\/\d+\/project\/\d+\/task\/(\d+)/,
      /^https?:\/\/app\.asana\.com\/0\/\d+\/(\d+)/,
    ],
    resolve: (match) => ({
      source: SuperSource.ASANA,
      id: match[1]!,
    }),
  },

  // Intercom: https://app.intercom.com/a/apps/{appId}/inbox/.../conversations/{id}
  [SuperSource.INTERCOM]: {
    patterns: [
      /^https?:\/\/app\.intercom\.com\/a\/apps\/[^/]+\/.*conversations\/(\d+)/,
    ],
    resolve: (match) => ({
      source: SuperSource.INTERCOM,
      id: match[1]!,
    }),
  },

  // SharePoint: ES rootId is composite "tenant.sharepoint.com,guid,guid" — not derivable from URL.
  [SuperSource.MICROSOFT_SHAREPOINT]: null,

  // HubSpot: https://app.hubspot.com/contacts/{portalId}/record/{objectType}/{recordId}
  [SuperSource.HUBSPOT]: {
    patterns: [
      /^https?:\/\/app\.hubspot\.com\/contacts\/(\d+)\/record\/[^/]+\/(\d+)/,
    ],
    resolve: (match) => ({
      source: SuperSource.HUBSPOT,
      id: match[2]!,
      rootId: match[1]!,
    }),
  },

  // Attio: https://app.attio.com/{org}/{objectType}/{recordId}/...
  //   or:  https://app.attio.com/{org}/calls/{callId}/{recordId}/transcript
  [SuperSource.ATTIO]: {
    patterns: [
      // Transcript: last path segment before /transcript is the record ID
      /^https?:\/\/app\.attio\.com\/[^/]+\/calls\/[^/]+\/([a-f0-9-]{36})\/transcript/,
      // Record/note: UUID in path position
      /^https?:\/\/app\.attio\.com\/[^/]+\/(?:company|person|deal|note)\/([a-f0-9-]{36})/,
    ],
    resolve: (match) => ({
      source: SuperSource.ATTIO,
      id: match[1]!,
    }),
  },

  // Slite: https://{org}.slite.com/app/docs/{noteId}/{optional-slug}
  //    or: https://{org}.slite.com/app/channels/{channelId}
  [SuperSource.SLITE]: {
    patterns: [
      /^https?:\/\/[^/]+\.slite\.com\/app\/(?:docs|channels)\/([A-Za-z0-9_-]+)/,
    ],
    resolve: (match) => ({
      source: SuperSource.SLITE,
      id: match[1]!,
    }),
  },
  [SuperSource.WEBSITE]: null,
  [SuperSource.FILE]: null,
  [SuperSource.GIT]: null,
  [SuperSource.CUSTOM]: null,
  [SuperSource.BIGQUERY]: null,
  [SuperSource.MCP]: null,
  [SuperSource.SALESFORCE]: null,
  [SuperSource.ZOOM]: null,
  [SuperSource.ZENDESK]: null,
  [SuperSource.MONDAY]: null,
  [SuperSource.MIRO]: null,
  [SuperSource.GRANOLA]: null,
  [SuperSource.AIRTABLE]: null,
  [SuperSource.CLICKUP]: null,
  [SuperSource.AMPLITUDE]: null,
  [SuperSource.BOX]: null,
  [SuperSource.SNOWFLAKE]: null,
  [SuperSource.NOTION_MCP]: null,
}

export function resolveUrl(url: string): UrlResolution | null {
  for (const resolver of Object.values(resolversBySource)) {
    if (!resolver) continue
    for (const pattern of resolver.patterns) {
      const match = url.match(pattern)
      if (match) {
        return resolver.resolve(match)
      }
    }
  }
  return null
}
